<template>
    <v-container>
        <page-header :title="`${mode === 'edit' ? 'Edit' : mode === 'show' ? '' : 'New'} Arena`.trim()" icon="mdi-stadium-outline" />
        <v-card class="form-card" elevation="1">
            <v-form :readonly="readOnly">
                <div class="form-section-title">
                    <v-icon small>mdi-stadium-outline</v-icon>Details
                </div>
                <v-row align="center">
                    <v-col cols="12" md="8">
                        <v-text-field
                            v-model="arena.name"
                            outlined
                            dense
                            hide-details="auto"
                            label="Name"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-switch
                            v-model="arena.active"
                            class="mt-0 pt-0"
                            color="primary"
                            hide-details
                            inset
                            :label="arena.active ? 'Active' : 'Not Active'"
                        />
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-map-marker-outline</v-icon>Location
                </div>
                <v-row>
                    <v-col cols="12" md="8">
                        <v-text-field
                            v-model="arena.address"
                            outlined
                            dense
                            hide-details="auto"
                            label="Address"
                            prepend-inner-icon="mdi-map-marker-outline"
                            :clearable="clearable"
                        />
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field
                            v-model="arena.coordinates"
                            outlined
                            dense
                            hide-details="auto"
                            label="Coordinates"
                            placeholder="lat, lng"
                            prepend-inner-icon="mdi-crosshairs-gps"
                            :clearable="clearable"
                        >
                            <template v-if="arena.coordinates" v-slot:append-outer>
                                <v-btn icon small :href="`https://www.google.com/maps?q=${encodeURIComponent(arena.coordinates)}`" target="_blank" title="Open in Google Maps">
                                    <v-icon small>mdi-open-in-new</v-icon>
                                </v-btn>
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>

                <div class="form-section-title">
                    <v-icon small>mdi-image-outline</v-icon>Image
                </div>
                <v-row>
                    <v-col cols="12" md="6">
                        <image-upload
                            v-model="image"
                            :src="imagePath"
                            label="Arena Image"
                            :readonly="readOnly"
                            @change="changeImage"
                            @clear="clearImage"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card>
        <div v-if="mode !== 'show'" class="form-actions">
            <v-spacer />
            <v-btn large text @click="$router.go(-1)">
                Cancel
            </v-btn>
            <v-btn :loading="loading" :disabled="shouldDisableSubmit()" class="white--text" large depressed color="black" @click="onSubmit">
                <v-icon left>mdi-content-save-outline</v-icon>Save
            </v-btn>
        </div>
        <alert :show-alert="showAlert" :message="message" :type="type" />
    </v-container>
</template>

<script>
    import PageHeader from '@/components/PageHeader'
    import ImageUpload from '@/components/ImageUpload'
    import arenaService from '@/services/arena'
    import Alert from '@/components/Alert'

    export default {
        name: 'ArenaForm',
        components: { Alert, PageHeader, ImageUpload },
        data: () => {
            return {
                showAlert: false,
                message: null,
                type: null,
                mode: null,
                googleApiHost: 'https://storage.googleapis.com',
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                arena: {
                },
                arenaId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                categories: [],
                image: {
                    name: 'Image file'
                },
                imageChanged: false,
                imagePath: ''
            }
        },
        async created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            this.arenaId = this.$route.params.arenaId
            if (this.arenaId) {
                arenaService.getArena(this.arenaId).then(resp => {
                    this.arena = resp.data
                    if (this.arena.image) {
                        this.imagePath = this.googleApiHost + '/' + this.arena.image
                    }
                })
            }
        },
        methods: {
            changeImage() {
                this.imageChanged = true
                if (this.image) {
                    this.imagePath = URL.createObjectURL(this.image)
                }
            },
            clearImage() {
                this.imagePath = ''
                this.arena.image = null
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            shouldDisableSubmit() {
                return false
            },
            setStartDate(date) {
                this.startDate = date
            },
            setEndDate(date) {
                this.endDate = date
            },
            onEdit() {
                this.$router.push(`/arenas/${this.arenaId}/edit`)
            },
            createArena() {
                arenaService.createArena(this.arena).then(arenaResponse => {
                    if (arenaResponse.id) {
                        this.arenaId = arenaResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/arenas`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            editArena() {
                arenaService.updateArena(this.arenaId, this.arena).then(arenaResponse => {
                    if (arenaResponse.id) {
                        this.arenaId = arenaResponse.id
                    }
                    this.loading = false
                    this.$router.push(`/arenas`)
                }).catch((error) => {
                    this.loading = false
                    this.setAlert(error.response.data.message, 'error')
                })
            },
            uploadImage() {
                if (!this.imageChanged || this.image == null) {
                    return
                }
                let formData = new FormData()
                formData.append('file', this.image)
                return arenaService.uploadFile(formData)
            },
            async onSubmit() {
                this.loading = true
                const uploadImageResponse = await this.uploadImage()
                const imageUrl = uploadImageResponse?.data.content || null
                if (imageUrl) {
                    this.arena.image = imageUrl
                }
                if (this.mode === 'edit') {
                    this.editArena()
                } else if (this.mode === 'create') {
                    this.createArena()
                }
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
